import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { errorCodes } from "../../../convex/errorCodes";
import { LoadingState } from "../components/ui/LoadingState";
import { ErrorState } from "../components/ui/ErrorState";
import { BILLING_ERROR_CODES, buildLoggedBillingPath } from "../lib/billingGuards";
import { WEB_ACTION_TIMEOUT_MS, withTimeout } from "../lib/withTimeout";
import { t } from "../i18n";
import { isValidInstagramHandle, normalizeInstagramHandle } from "@lib/utils";

type CreateMonitorResult = {
  success?: boolean;
  error?: string;
};

function lookupFriendlyMessage(code: string | undefined): string {
  if (!code) return errorCodes.TARGET_INFO_UNAVAILABLE.friendlyMessage;
  const entry = Object.values(errorCodes).find(
    (v) => typeof v !== "function" && v.code === code,
  );
  return entry && typeof entry !== "function"
    ? entry.friendlyMessage
    : errorCodes.TARGET_INFO_UNAVAILABLE.friendlyMessage;
}

export function AppAddTargetProcessingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const createMonitor = useAction(api.monitors.createMonitorAfterPurchase);
  const didStartRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);

  const handle = useMemo(() => {
    const raw = searchParams.get("handle") ?? "";
    const normalized = normalizeInstagramHandle(raw);
    return isValidInstagramHandle(normalized) ? normalized : "";
  }, [searchParams]);

  const steps = [
    t("loadingAddMonitorStep1"),
    t("loadingAddMonitorStep2"),
    t("loadingAddMonitorStep3"),
  ];

  useEffect(() => {
    const timers = [
      window.setTimeout(() => setCurrentStep(1), 900),
      window.setTimeout(() => setCurrentStep(2), 2000),
    ];
    return () => timers.forEach(window.clearTimeout);
  }, []);

  useEffect(() => {
    if (didStartRef.current) return;
    didStartRef.current = true;

    if (!handle) {
      navigate("/app/add-target", { replace: true });
      return;
    }

    withTimeout(
      createMonitor({ handle, source: "home" }),
      WEB_ACTION_TIMEOUT_MS,
      "Web monitor action timed out",
    )
      .then((result) => {
        const payload = result as CreateMonitorResult | undefined;
        if (payload?.success) {
          navigate("/app", { replace: true });
          return;
        }

        const code = payload?.error;
        if (code && BILLING_ERROR_CODES.has(code)) {
          navigate(buildLoggedBillingPath({ handle, reason: code }), {
            replace: true,
          });
          return;
        }

        setError(lookupFriendlyMessage(code));
      })
      .catch((err: unknown) => {
        const code = (err as { data?: { code?: string } })?.data?.code;
        if (code && BILLING_ERROR_CODES.has(code)) {
          navigate(buildLoggedBillingPath({ handle, reason: code }), {
            replace: true,
          });
          return;
        }
        setError(lookupFriendlyMessage(code));
      });
  }, [createMonitor, handle, navigate]);

  if (error) {
    return (
      <div className="app-page app-processing-page">
        <ErrorState
          message={error}
          action={{
            label: t("commonBack"),
            onClick: () => navigate("/app/add-target"),
          }}
        />
      </div>
    );
  }

  return (
    <div className="app-page app-processing-page">
      <LoadingState
        title={t("loadingAddMonitorTitle")}
        subtitle={t("loadingAddMonitorSubtitle")}
        steps={steps}
        currentStep={currentStep}
      />
    </div>
  );
}
